/**
 * Resident widgets (screenshots/73 "Residents"; 69): Your alderperson, How would you vote?, Your alderperson's votes,
 * Neighbors who spoke. How would you vote? saves through the Legislation stance endpoint (one position per person
 * per matter; offices only ever see ward totals, §6 ticket 17).
 */
import { useState } from "react";
import { Face } from "../../components/shared/Face";
import { OvIcon } from "../icons";
import { OvChip, Overflow, Row, Stat, fit, go } from "./parts";
import type { WidgetProps } from "./props";

export function YourAlderperson({ feeds, w }: WidgetProps) {
  const a = feeds.yourAlderperson;
  if (!a) return null;
  // Even at 6 × 2 (an S height) the row with the face and Call office fits (69); only narrow tiles become a stat.
  if (w <= 3) return <Stat value={`Ward ${a.ward}`} sub={a.name} />;
  return (
    <div className="ov-alder">
      <a href={`/dashboard/people/${a.personId}`} onClick={go(`/dashboard/people/${a.personId}`)} className="ov-alder__face"><Face name={a.name} photoUrl={a.photoUrl} size={48} /></a>
      <span className="ov-alder__text">
        <span className="ov-alder__name">{a.name}</span>
        {a.summary && <span className="ov-row__sub">{a.summary}</span>}
      </span>
      {a.phone && <a className="ov-call ov-call--primary" href={`tel:${a.phone.replace(/[^\d+]/g, "")}`}><OvIcon name="phone" />Call office</a>}
    </div>
  );
}

type Stance = "support" | "oppose" | null;

export function HowWouldYouVote({ feeds, h }: WidgetProps) {
  const f = feeds.howWouldYouVote;
  const [stances, setStances] = useState<Record<string, Stance>>(() => Object.fromEntries((f?.items ?? []).map((i) => [i.recordId, i.stance])));
  if (!f) return null;
  const shown = f.items.slice(0, fit(h, 70, 20));
  const choose = (id: string, v: "support" | "oppose") => {
    const next = stances[id] === v ? null : v;
    setStances((s) => ({ ...s, [id]: next }));
    void fetch(`/api/dashboard/legislation/${encodeURIComponent(id)}/stance`, { method: next ? "PUT" : "DELETE", credentials: "include",
      headers: { "content-type": "application/json" }, body: JSON.stringify({ position: next }) }).catch(() => {});
  };
  return (
    <div className="ov-rows ov-rows--spread">
      {shown.map((i) => (
        <Row key={i.recordId} title={i.title} sub={i.sub} right={
          <span className="ov-seg" role="radiogroup" aria-label={`How would you vote on ${i.title}?`}>
            {(["support", "oppose"] as const).map((v) => (
              <button key={v} role="radio" aria-checked={stances[i.recordId] === v} className={`ov-seg__opt ov-seg__opt--${v}`} onClick={() => choose(i.recordId, v)}>
                {v === "support" ? "Support" : "Oppose"}
              </button>
            ))}
          </span>
        } />
      ))}
      <p className="ov-note">One person, one position. Your name is never shared.</p>
    </div>
  );
}

export function AlderVotes({ feeds, h }: WidgetProps) {
  const votes = feeds.alderVotes?.votes ?? [];
  const shown = votes.slice(0, fit(h, 66));
  return (
    <div className="ov-rows ov-rows--spread">
      {shown.map((v) => (
        <Row key={v.recordId} title={v.title} sub={v.sub} href={`/dashboard/legislation/${v.recordId}`}
          right={<OvChip chip={{ text: v.vote === "yea" ? "YEA" : v.vote === "nay" ? "NAY" : "ABSENT", tone: v.vote === "yea" ? "positive" : v.vote === "nay" ? "negative" : "neutral" }} />} />
      ))}
      <Overflow n={votes.length - shown.length} />
    </div>
  );
}

/** Public comments from your ward (69). Residents get no video frames or profile links (brief rule 4: offices only). */
export function NeighborsSpoke({ feeds, w }: WidgetProps) {
  const f = feeds.neighborsSpoke;
  if (!f) return null;
  const perRow = Math.max(1, Math.floor((w * 96 - 16) / 220));
  const shown = f.people.slice(0, perRow);
  return (
    <div className="ov-neighbors">
      {shown.map((p) => (
        <a key={p.key} className="ov-neighbor" href={p.href} onClick={go(p.href)}>
          {p.frameUrl ? <img className="ov-neighbor__frame" src={p.frameUrl} alt="" loading="lazy" /> : <span className="ov-neighbor__frame ov-neighbor__frame--none" aria-hidden><OvIcon name="mic" /></span>}
          <span className="ov-row__text"><span className="ov-neighbor__name">{p.name}</span><span className="ov-row__sub">{p.topics}</span></span>
        </a>
      ))}
    </div>
  );
}
